// src/lib/calendar.ts — Calendar: shapes, calls, kind colours and the date arithmetic
// the month / week / day views share. Dates are local YYYY-MM-DD strings; times are
// shown in the browser's own time zone, which is also sent to the API as `tz`.
import { api } from './api';

export type EventKind =
  | 'WORK_ORDER' | 'VIEWING' | 'INSPECTION' | 'MOVE_IN' | 'MOVE_OUT' | 'CERTIFICATE' | 'ASSET_SERVICE' | 'CONTRACT'
  | 'MEETING' | 'SITE_VISIT' | 'CONTRACTOR' | 'OTHER';
export type ManualKind = 'VIEWING' | 'MEETING' | 'SITE_VISIT' | 'CONTRACTOR' | 'OTHER';

export interface CalEvent {
  id: string;
  kind: EventKind;
  kindLabel: string;
  title: string;
  detail: string | null;
  start: string;
  end: string;
  allDay: boolean;
  day: string;
  url: string | null;
  propertyId: string | null;
  propertyName: string | null;
  unitId: string | null;
  unitRef: string | null;
  assigneeId: string | null;
  assignee: string | null;
  source: 'derived' | 'manual';
  record: { resource: string; id: string };
  status: string | null;
  tenancyId?: string | null;
  notes?: string | null;
  editable: boolean;
}

export interface CalResult { from: string; to: string; tz: string; total: number; counts: Partial<Record<EventKind, number>>; events: CalEvent[] }
export interface CalPerson { id: string; name: string; roleName: string | null }

export interface BookBody {
  kind: ManualKind;
  title: string;
  allDay: boolean;
  startsAt: string;
  endsAt?: string | null;
  durationMinutes?: number | null;
  tz: string;
  propertyId?: string | null;
  unitId?: string | null;
  assignedTo?: string | null;
  notes?: string | null;
}

/** One colour per kind — the chip, the agenda rule and the legend all use it. */
export const KINDS: Array<{ key: EventKind; label: string; colour: string; manual?: boolean }> = [
  { key: 'WORK_ORDER', label: 'Work order', colour: '#4f86e0' },
  { key: 'VIEWING', label: 'Viewing', colour: '#2fa97a', manual: true },
  { key: 'INSPECTION', label: 'Inspection', colour: '#6f7ce8' },
  { key: 'MOVE_IN', label: 'Move in', colour: '#1f9d63' },
  { key: 'MOVE_OUT', label: 'Move out', colour: '#e0962f' },
  { key: 'CERTIFICATE', label: 'Certificate', colour: '#d9534f' },
  { key: 'ASSET_SERVICE', label: 'Servicing', colour: '#3aa6b8' },
  { key: 'CONTRACT', label: 'Contract', colour: '#b5832f' },
  { key: 'MEETING', label: 'Meeting', colour: '#8b6fd6', manual: true },
  { key: 'SITE_VISIT', label: 'Site visit', colour: '#a07a4f', manual: true },
  { key: 'CONTRACTOR', label: 'Contractor', colour: '#c4673a', manual: true },
  { key: 'OTHER', label: 'Appointment', colour: '#7d8fa3', manual: true },
];
export const KIND_BY_KEY = Object.fromEntries(KINDS.map((k) => [k.key, k])) as Record<EventKind, (typeof KINDS)[number]>;
export const colourOf = (k: EventKind) => KIND_BY_KEY[k]?.colour ?? '#7d8fa3';

export const browserTz = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/London'; } catch { return 'Europe/London'; } };

export const calendarApi = {
  list: async (q: { from: string; to: string; staffId?: string; kind?: string; propertyId?: string }) =>
    (await api.get<CalResult>('/calendar', { ...q, tz: browserTz() })).data,
  people: async () => (await api.get<CalPerson[]>('/calendar/people')).data,
  book: async (body: BookBody) => (await api.post<CalEvent>('/calendar', body)).data,
  update: async (id: string, body: Partial<BookBody>) => (await api.patch<CalEvent>(`/calendar/${id}`, body)).data,
  cancel: (id: string) => api.delete<{ id: string; cancelled: true }>(`/calendar/${id}`),
};

/** Nav probe: does a read-only calendar user (a resident) have anything in the next 90 days? */
export async function calendarHasAnything(): Promise<boolean> {
  const from = isoToday();
  const r = await calendarApi.list({ from, to: addDays(from, 90) });
  return r.total > 0;
}

// ─── Date arithmetic on local YYYY-MM-DD ─────────────────────────────────────

const pad = (n: number) => String(n).padStart(2, '0');
export const isoOf = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const isoToday = () => isoOf(new Date());
const parse = (iso: string) => { const [y, m, d] = iso.split('-').map(Number); return new Date(y!, (m ?? 1) - 1, d ?? 1, 12); };
export const addDays = (iso: string, n: number) => { const d = parse(iso); d.setDate(d.getDate() + n); return isoOf(d); };
export const addMonths = (iso: string, n: number) => { const d = parse(iso); d.setDate(1); d.setMonth(d.getMonth() + n); return isoOf(d); };
export const monthStart = (iso: string) => `${iso.slice(0, 8)}01`;
export const monthEnd = (iso: string) => addDays(addMonths(monthStart(iso), 1), -1);
/** Monday on or before the date (weeks start on Monday). */
export const weekStart = (iso: string) => addDays(iso, -((parse(iso).getDay() + 6) % 7));
export const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const fmt = (iso: string, o: Intl.DateTimeFormatOptions) => parse(iso).toLocaleDateString('en-GB', o);
export const timeOf = (instant: string) => new Date(instant).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
/** Minutes after local midnight of an instant. */
export const minutesOf = (instant: string) => { const d = new Date(instant); return d.getHours() * 60 + d.getMinutes(); };

/** Every local day an event covers within [from, to] (an all-day booking can span days). */
export function daysOf(e: CalEvent, from: string, to: string): string[] {
  const first = e.allDay ? e.start : e.day;
  const last = e.allDay ? e.end : isoOf(new Date(new Date(e.end).getTime() - 1));
  const out: string[] = [];
  for (let d = first < from ? from : first; d <= last && d <= to; d = addDays(d, 1)) out.push(d);
  return out.length ? out : [e.day];
}
